import {
  addOfferDocumentSource,
  changeOfferStatus,
  generateDocumentSigningLink,
  getAllOffers,
  getDocumentSigningState,
  getOffer,
  getOfferDocumentSources,
  getOfferDocumentStatus,
  getOfferWithSignature,
  getUserOffers,
  removeOfferDocumentSource,
  sendDocumentForSigning,
  signOfferDocument,
  submitOffer,
  updateOffer,
  uploadOfferDocument,
  type DocumentSigningLinkResult,
  type DocumentSigningState,
  type Offer,
  type OfferDocumentComposition,
  type OfferDocumentResult,
  type OfferDocumentStatus,
  type OfferListItem,
  type OfferListItemWithCompany,
  type OfferResponse,
  type OfferSubmitData,
  type SendDocumentResult,
  type SignOfferDocumentPayload,
  type SigningLinkKind,
} from '@lib/api';
import { type StagedDocumentSource } from '@components/offer/LegalDocumentTab';
import { toProblem } from '@lib/api/base';
import { type Problem } from '@lib/api/types';
import { toast } from '@ui/use-toast';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';

/**
 * Hook for submitting a new offer
 */
export const useSubmitOffer = (
  options?: Omit<UseMutationOptions<Offer, Problem, OfferSubmitData>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();

  return useMutation<Offer, Problem, OfferSubmitData>({
    mutationFn: data => submitOffer(data),
    ...(options ?? {}),
    onSuccess: (offer, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      queryClient.invalidateQueries({ queryKey: ['offer', offer.id] });
      options?.onSuccess?.(offer, variables, context);
    },
    onError: (error, variables, context) => {
      console.error('Error creating offer:', error);
      options?.onError?.(error, variables, context);
    },
  });
};

/**
 * Hook for uploading/replacing an offer's legal document (recruiter).
 * Feedback toasts are handled by the calling component.
 */
export const useUploadOfferDocument = (offerId: string) => {
  const queryClient = useQueryClient();

  return useMutation<OfferDocumentResult, Problem, File>({
    mutationFn: (file: File) => uploadOfferDocument(offerId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offer', offerId] });
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    },
  });
};

/**
 * Hook for fetching an offer's document composition (ordered source list).
 * Enable only in edit mode where an offer id exists.
 */
export const useOfferDocumentSources = (offerId: string | undefined, enabled = true) => {
  return useQuery<OfferDocumentComposition, Problem>({
    queryKey: ['offer', offerId, 'document-sources'],
    queryFn: async () => {
      if (!offerId) throw new Error('Offer ID is required');
      try {
        return await getOfferDocumentSources(offerId);
      } catch (err) {
        throw toProblem(err);
      }
    },
    enabled: enabled && !!offerId,
  });
};

const invalidateOfferComposition = (
  queryClient: ReturnType<typeof useQueryClient>,
  offerId: string
) => {
  queryClient.invalidateQueries({ queryKey: ['offer', offerId, 'document-sources'] });
  queryClient.invalidateQueries({ queryKey: ['offer', offerId] });
  queryClient.invalidateQueries({ queryKey: ['offers'] });
};

/**
 * Write the mutation's returned composition into the cache synchronously, then
 * refresh the sibling offer keys. Invalidate-only leaves a one-round-trip
 * window after the pending state clears where checkboxes/dropzone still show
 * the stale composition — inviting a duplicate re-add (409 for templates, a
 * REAL duplicate source for one-off uploads).
 */
const applyOfferComposition = (
  queryClient: ReturnType<typeof useQueryClient>,
  offerId: string,
  composition: OfferDocumentComposition
) => {
  queryClient.setQueryData(['offer', offerId, 'document-sources'], composition);
  queryClient.invalidateQueries({ queryKey: ['offer', offerId] });
  queryClient.invalidateQueries({ queryKey: ['offers'] });
};

/**
 * Error toast for composition mutations. Lives at HOOK level, not mutate()
 * level: React Query v5 drops mutate-level callbacks when the component
 * unmounts, and a recruiter who switches tabs mid-conversion must still learn
 * the attach failed. Success toasts stay component-level (benign if dropped).
 */
const compositionErrorToast = (error: Problem, fallback: string) =>
  toast({
    // DOCUMENT_COMPLETED is a deliberate refusal, not a fault: the agreement is
    // already executed and its sealed copy is protected. "Something went wrong"
    // would tell the recruiter the app broke when it did exactly its job.
    title:
      error.code === 'DOCUMENT_COMPLETED' ? 'Agreement already signed' : 'Something went wrong',
    description: error.detail || fallback,
    variant: 'destructive',
  });

/**
 * Hook for adding a source (template or one-off upload) to an offer's document
 * composition. Success toasts are handled by the calling component; the error
 * toast is hook-level so it survives unmount.
 */
export const useAddOfferDocumentSource = (offerId: string) => {
  const queryClient = useQueryClient();

  return useMutation<OfferDocumentComposition, Problem, { templateId: string } | { file: File }>({
    mutationFn: source => addOfferDocumentSource(offerId, source),
    onSuccess: composition => applyOfferComposition(queryClient, offerId, composition),
    onError: error => compositionErrorToast(error, 'Failed to add the document'),
  });
};

/**
 * Hook for removing a source from an offer's document composition.
 * Success toasts are handled by the calling component; the error toast is
 * hook-level so it survives unmount.
 */
export const useRemoveOfferDocumentSource = (offerId: string) => {
  const queryClient = useQueryClient();

  return useMutation<OfferDocumentComposition, Problem, string>({
    mutationFn: sourceId => removeOfferDocumentSource(offerId, sourceId),
    onSuccess: composition => applyOfferComposition(queryClient, offerId, composition),
    onError: error => compositionErrorToast(error, 'Failed to remove the document'),
  });
};

/**
 * Hook for attaching a create-mode staged document composition to a freshly
 * created offer. Runs the staged sources in order with per-item error tolerance
 * (one attach failing does not abort the rest), then invalidates the offer +
 * document-sources query keys once. Resolves with the number of items that
 * failed so the caller can surface a partial-success toast; it never rejects.
 */
export const useAttachStagedSources = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { failures: number },
    Problem,
    { offerId: string; sources: StagedDocumentSource[] }
  >({
    mutationFn: async ({ offerId, sources }) => {
      let failures = 0;
      for (const source of sources) {
        try {
          await addOfferDocumentSource(
            offerId,
            source.kind === 'template' ? { templateId: source.templateId } : { file: source.file }
          );
        } catch {
          failures += 1;
        }
      }
      return { failures };
    },
    onSuccess: (_result, { offerId }) => invalidateOfferComposition(queryClient, offerId),
  });
};

/**
 * Hook for fetching an offer by ID
 */
export const useOffer = (offerId: string | undefined) => {
  return useQuery<OfferResponse, Error>({
    queryKey: ['offer', offerId],
    queryFn: () => {
      if (!offerId) throw new Error('Offer ID is required');
      return getOffer(offerId);
    },
    enabled: !!offerId,
  });
};

/**
 * Hook for fetching all user offers
 * @param refreshKey - Optional key to refresh data when changed
 */
export const useUserOffers = (refreshKey: number = 0) => {
  return useQuery<OfferListItem[], Error>({
    queryKey: ['offers', refreshKey],
    queryFn: getUserOffers,
  });
};

/**
 * Hook for fetching all offers from all companies (agency recruiters only)
 * @param refreshKey - Optional key to refresh data when changed
 */
export const useAllOffers = (refreshKey: number = 0) => {
  return useQuery<OfferListItemWithCompany[], Error>({
    queryKey: ['offers', 'all', refreshKey],
    queryFn: getAllOffers,
  });
};

/**
 * Hook for fetching an offer using a signed URL
 */
export const useOfferWithSignature = (signedData: string | undefined) => {
  return useQuery<OfferResponse, Error>({
    queryKey: ['offer', 'signature', signedData],
    queryFn: () => {
      if (!signedData) throw new Error('Signed data is required');
      return getOfferWithSignature(signedData);
    },
    enabled: !!signedData,
  });
};

/**
 * Hook for fetching the candidate's document signing state (public view only).
 * Enable only when in the public candidate view and a document is attached.
 */
export const useDocumentSigningState = (
  signedData: string | undefined,
  enabled: boolean,
  linkKind: SigningLinkKind = 'offer'
) => {
  return useQuery<DocumentSigningState, Problem>({
    queryKey: ['offer', 'signing-state', linkKind, signedData],
    queryFn: async () => {
      if (!signedData) throw new Error('Signed data is required');
      try {
        return await getDocumentSigningState(signedData, linkKind);
      } catch (err) {
        throw toProblem(err);
      }
    },
    enabled: enabled && !!signedData,
    retry: (failureCount, error) => {
      // Don't retry client errors (4xx) — expired/invalid links won't recover.
      if (error && 'status' in error && error.status >= 400 && error.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

/**
 * Hook for submitting document signatures (public view). Invalidates the
 * signing-state and the public offer queries so the ceremony and Accept gate
 * reflect the sealed document.
 */
export const useSignOfferDocument = (
  signedData: string | undefined,
  linkKind: SigningLinkKind = 'offer'
) => {
  const queryClient = useQueryClient();

  return useMutation<DocumentSigningState, Problem, SignOfferDocumentPayload>({
    mutationFn: payload => {
      if (!signedData) throw new Error('Signed data is required');
      return signOfferDocument(signedData, payload, linkKind);
    },
    onSuccess: data => {
      // The sign POST returns the fresh, authoritative signing state (identical
      // shape to the GET). Seed the signing-state cache with it synchronously so
      // the ceremony flips to the sealed/signed surface immediately — BEFORE the
      // invalidation-triggered refetch lands. Without this the UI can linger on
      // the ceremony (still `completed: false`) until the slower GET returns.
      // Key MUST match useDocumentSigningState's queryKey exactly.
      queryClient.setQueryData(['offer', 'signing-state', linkKind, signedData], data);
      queryClient.invalidateQueries({
        queryKey: ['offer', 'signing-state', linkKind, signedData],
      });
      queryClient.invalidateQueries({ queryKey: ['offer', 'signature', signedData] });
    },
  });
};

/**
 * Hook for the recruiter's document-status drawer/card. Authoritative view
 * (the list badge is the cheap approximation).
 */
export const useOfferDocumentStatus = (offerId: string | undefined, enabled = true) => {
  return useQuery<OfferDocumentStatus, Problem>({
    queryKey: ['offer', offerId, 'document-status'],
    queryFn: async () => {
      if (!offerId) throw new Error('Offer ID is required');
      try {
        return await getOfferDocumentStatus(offerId);
      } catch (err) {
        throw toProblem(err);
      }
    },
    enabled: enabled && !!offerId,
    retry: (failureCount, error) => {
      if (error && 'status' in error && error.status >= 400 && error.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

/**
 * Hook for minting a signing-only link (screenshare flow). The mint pins the
 * document server-side, so invalidate the status views on success. Feedback
 * (dialog with visible link + copy button — never await-then-write to the
 * clipboard, Safari blocks it) is handled by the calling component.
 */
export const useGenerateDocumentSigningLink = (offerId: string) => {
  const queryClient = useQueryClient();

  return useMutation<DocumentSigningLinkResult, Problem, void>({
    mutationFn: async () => {
      try {
        return await generateDocumentSigningLink(offerId);
      } catch (err) {
        throw toProblem(err);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offer', offerId, 'document-status'] });
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    },
  });
};

/**
 * Hook for emailing the candidate a signing-only link. A resolved mutation may
 * still carry `warnings` (email failed, link ready to copy) — callers MUST
 * check `data.sent` and surface warnings as a destructive toast.
 */
export const useSendDocumentForSigning = (offerId: string) => {
  const queryClient = useQueryClient();

  return useMutation<SendDocumentResult, Problem, void>({
    mutationFn: async () => {
      try {
        return await sendDocumentForSigning(offerId);
      } catch (err) {
        throw toProblem(err);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offer', offerId, 'document-status'] });
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    },
  });
};

/**
 * Hook for updating an existing offer
 */
export const useUpdateOffer = (offerId: string) => {
  const queryClient = useQueryClient();

  return useMutation<Offer, Problem, OfferSubmitData>({
    mutationFn: data => updateOffer(offerId, data),
    onSuccess: () => {
      // Invalidate both the single offer query and the user offers list
      queryClient.invalidateQueries({ queryKey: ['offer', offerId] });
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    },
    onError: error => {
      console.error('Error updating offer:', error);
    },
  });
};

/**
 * Hook for changing offer status
 */
export const useChangeOfferStatus = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { status: string },
    Problem,
    { offerId: string; newStatus: string; timestamp?: string }
  >({
    mutationFn: ({ offerId, newStatus, timestamp }) =>
      changeOfferStatus(offerId, newStatus, timestamp),
    onSuccess: (_data, variables) => {
      // Invalidate both the single offer query and the user offers list
      queryClient.invalidateQueries({ queryKey: ['offer', variables.offerId] });
      queryClient.invalidateQueries({ queryKey: ['offers'] });
    },
    onError: error => {
      console.error('Error changing offer status:', error);
    },
  });
};
